import React from 'react';
import Animated, { useAnimatedRef, useScrollViewOffset, useSharedValue } from 'react-native-reanimated';

import { Box } from '@/components/lib/components/Box';
import { PromoHeaderContent } from '@/feature/promohub/components/PromoHeaderContent';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import { PromotionThemeDocument } from '@/types/prismic.generated';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { usePromoTheme } from '../hooks/use-promo-theme';
import GradientBackground from './GradientBackground';
import { PromoHeaderBackground } from './PromoHeader';

export type HeaderProps = {
    showNavigationButton?: boolean;
    showRafHistory?: boolean;
    title?: string | null;
    icon: string;
    buttonCta?: string;
    buttonPostCta?: string;
    expirationTag?: string;
    supportingText?: string | null;
    showButton?: boolean;
    buttonTestID?: string;
    refferalSummaryCard?: React.ReactNode;
    buttonLoading?: boolean;
    isOptedIn?: boolean;
    onPress: () => void;
    infoIconPress?: () => void;
    historyIconPress?: () => void;
    customCta?: string;
    showCustomCta?: boolean;
    onCustomCtaPress?: () => void;
};

export const PromoDetailsPage = ({
    theme,
    pageContent,
    headerProps,
}: {
    theme: PromotionThemeDocument['data'];
    pageContent?: React.ReactNode;
    headerProps: HeaderProps;
}) => {
    const { background_color } = usePromoTheme(theme);

    const headerHeight = useSharedValue(0);

    const scrollRef = useAnimatedRef<Animated.ScrollView>();
    const scrollViewOffset = useScrollViewOffset(scrollRef);

    const {
        title,
        icon,
        supportingText,
        expirationTag,
        showButton,
        buttonTestID,
        showNavigationButton,
        showRafHistory,
        isOptedIn,
        buttonPostCta,
        buttonCta,
        buttonLoading,
        onPress,
        refferalSummaryCard,
        infoIconPress,
        historyIconPress,
        customCta,
        showCustomCta,
        onCustomCtaPress,
    } = headerProps;

    return (
        <GradientBackground firstColor={background_color}>
            <Box flex={1} width={'100%'}>
                <PromoHeaderBackground
                    theme={theme}
                    headerHeight={headerHeight}
                    scrollY={scrollViewOffset}
                    showNavigationButton={showNavigationButton}
                    showRafHistory={showRafHistory}
                    infoIconPress={infoIconPress}
                    historyIconPress={historyIconPress}
                />
                <Animated.ScrollView
                    ref={scrollRef}
                    scrollEventThrottle={16}
                    showsVerticalScrollIndicator={showScrollIndicator}
                    contentContainerStyle={common.grow}
                >
                    <PromoHeaderContent
                        title={title}
                        icon={icon}
                        scrollY={scrollViewOffset}
                        headerHeight={headerHeight}
                        theme={theme}
                        supportingText={supportingText}
                        expirationTag={expirationTag}
                        showButton={showButton}
                        buttonTestID={buttonTestID}
                        isOptedIn={isOptedIn}
                        buttonPostCta={buttonPostCta}
                        buttonCta={buttonCta}
                        onPress={onPress}
                        buttonLoading={buttonLoading}
                        refferalSummaryCard={refferalSummaryCard}
                        customCta={customCta}
                        showCustomCta={showCustomCta}
                        onCustomCtaPress={onCustomCtaPress}
                    />
                    <MaxWidthWrapper flex={1}>{pageContent}</MaxWidthWrapper>
                </Animated.ScrollView>
            </Box>
        </GradientBackground>
    );
};
